import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Box,
  Check,
  CheckCircle2,
  ChevronDown,
  Lock,
  Maximize2,
  Minimize2,
  Monitor,
  Nfc,
  RotateCcw,
  QrCode,
  ShoppingCart,
  Sparkles,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { qrPrintApi } from '../../api/qrPrintApi';
import QrProduct3DViewer from '../../components/qrPrint/3d/QrProduct3DViewer';
import QrDynamicCustomizationPanel from '../../components/qrPrint/editor/QrDynamicCustomizationPanel';
import {
  normalizeQrEditorSchema,
} from '../../components/qrPrint/admin/qrEditorSchema.jsx';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  formatMoney,
  getOptionLabel,
  getOptionValue,
  getUserEstablishmentId,
} from '../../utils/qrPrintHelpers';

import '../../styles/QrPrint/QrPrintDesigner.css';

const translateFallback = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};


const PRINT_SIZE_MIN_CM = 3;
const PRINT_SIZE_MAX_CM = 20;

const roundDesignNumber = (value) =>
  Math.round(Number(value) * 100) / 100;

const formatCm = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '0cm';
  }

  return `${Number(number.toFixed(2))}cm`;
};

const normalizeHexColor = (value, fallback) => {
  if (typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)) {
    return value;
  }

  return fallback;
};

const getTextTransform = (align) => {
  if (align === 'left') {
    return 'translate(0, -50%)';
  }

  if (align === 'right') {
    return 'translate(-100%, -50%)';
  }

  return 'translate(-50%, -50%)';
};

const parseCmSizeFromText = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const match = value
    .replace(',', '.')
    .match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*[x×*]\s*(\d+(?:\.\d+)?)\s*(?:cm)?/i);

  if (!match) {
    return null;
  }

  return {
    width: clamp(match[1], PRINT_SIZE_MIN_CM, PRINT_SIZE_MAX_CM),
    height: clamp(match[2], PRINT_SIZE_MIN_CM, PRINT_SIZE_MAX_CM),
  };
};

const getProductSizeCmFromOption = (option) => {
  if (!option || typeof option !== 'object') {
    return null;
  }

  const width =
    option.width_cm ??
    option.widthCm ??
    option.width ??
    option.w;

  const height =
    option.height_cm ??
    option.heightCm ??
    option.height ??
    option.h;

  if (Number.isFinite(Number(width)) && Number.isFinite(Number(height))) {
    return {
      width: clamp(width, PRINT_SIZE_MIN_CM, PRINT_SIZE_MAX_CM),
      height: clamp(height, PRINT_SIZE_MIN_CM, PRINT_SIZE_MAX_CM),
    };
  }

  return (
    parseCmSizeFromText(option.value) ||
    parseCmSizeFromText(option.label) ||
    parseCmSizeFromText(option.name) ||
    parseCmSizeFromText(option.title)
  );
};

const getSelectedProductSizeCm = (product, selectedSize) => {
  const options = product?.available_sizes || [];
  const selectedOption = options.find(
    (option) => String(getOptionValue(option)) === String(selectedSize),
  );

  return getProductSizeCmFromOption(selectedOption);
};

const getDefaultDesignerPrintSizeCm = (productType) => {
  if (productType === 'wall_sign') {
    return { width: 20, height: 20 };
  }

  if (productType === 'counter_display') {
    return { width: 15, height: 20 };
  }

  if (productType === 'table_stand' || productType === 'menu_holder') {
    return { width: 12, height: 18 };
  }

  return { width: 15, height: 10 };
};

const getDesignerPrintSizeCm = ({
  schema,
  configuration,
  product,
  selectedSize,
}) => {
  const selectedSizeCm = getSelectedProductSizeCm(product, selectedSize);
  const canvas =
    configuration?.canvas ||
    schema?.canvas ||
    schema?.print_size ||
    {};
  const fallback =
    selectedSizeCm ||
    getDefaultDesignerPrintSizeCm(product?.product_type);

  return {
    width: clamp(
      canvas.width_cm ?? fallback.width,
      PRINT_SIZE_MIN_CM,
      PRINT_SIZE_MAX_CM,
    ),
    height: clamp(
      canvas.height_cm ?? fallback.height,
      PRINT_SIZE_MIN_CM,
      PRINT_SIZE_MAX_CM,
    ),
  };
};

const getBlocksForDesignerSide = (schema, side = 'front') =>
  (schema?.blocks || []).filter((block) => {
    if (!block || block.enabled === false) {
      return false;
    }

    if (block.side && block.side !== side) {
      return false;
    }

    if (!block.side && side === 'back') {
      return false;
    }

    return [
      'text',
      'logo',
      'image',
      'qr',
      'nfc',
      'shape',
      'background_color',
      'product_color',
    ].includes(block.type);
  });

const getDesignerBlockValue = (configuration, block) => {
  const elementValue = configuration?.editor_elements?.[block.id];

  if (
    elementValue !== undefined &&
    elementValue !== null &&
    typeof elementValue !== 'object'
  ) {
    return elementValue;
  }

  if (typeof block.default !== 'object' || block.default === null) {
    return elementValue ?? block.default ?? {};
  }

  return {
    ...clonePlain(block.default),
    ...(typeof elementValue === 'object' && elementValue !== null
      ? elementValue
      : {}),
  };
};

const makeDesignerBlock = (type) => {
  const id = `${type}-${Date.now()}-${Math.round(Math.random() * 9999)}`;

  if (type === 'text') {
    return {
      id,
      type: 'text',
      label: 'Custom text',
      enabled: true,
      required: false,
      default: {
        text: 'New text',
        x: 50,
        y: 34,
        font_size: 3.6,
        color: '#1f2937',
        align: 'center',
        weight: 700,
      },
    };
  }

  if (type === 'qr') {
    return {
      id,
      type: 'qr',
      label: 'QR code',
      enabled: true,
      required: false,
      default: {
        x: 50,
        y: 55,
        size: 34,
        foreground_color: '#111111',
        background_color: '#ffffff',
      },
    };
  }

  if (type === 'nfc') {
    return {
      id,
      type: 'nfc',
      label: 'NFC tap zone',
      enabled: true,
      required: false,
      default: {
        x: 50,
        y: 66,
        size: 22,
        color: '#d97706',
        background_color: '#fff7ed',
      },
    };
  }

  return {
    id,
    type: 'shape',
    label: 'Shape',
    enabled: true,
    required: false,
    default: {
      shape: 'rectangle',
      x: 50,
      y: 50,
      width: 28,
      height: 12,
      radius: 14,
      fill_color: '#fff7ed',
      border_color: '#d97706',
      opacity: 1,
    },
  };
};

const getDragPadding = (block, value) => {
  if (block.type === 'qr') {
    const half = clamp(value.size, 12, 72) / 2;
    return { x: half, y: half };
  }

  if (block.type === 'logo' || block.type === 'image') {
    const half = clamp(value.width, 6, 70) / 2;
    return { x: half, y: half };
  }

  if (block.type === 'nfc') {
    const half = clamp(value.size, 10, 48) / 2;
    return { x: half, y: half };
  }

  if (block.type === 'shape') {
    return {
      x: clamp(value.width, 2, 100) / 2,
      y: clamp(value.height, 1, 100) / 2,
    };
  }

  return { x: 3, y: 3 };
};


const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';
const SELECTED_MENU_ESTABLISHMENT_STORAGE_KEY =
  'selected_menu_establishment_id';

const DEFAULT_CONFIGURATION = {
  preview_mode: '2d',
  restaurant_logo: true,
  restaurant_name: '',
  custom_text: 'Scan to view our menu',
  background_color: '#ffffff',
  text_color: '#1f2937',
  accent_color: '#d97706',
  qr: {
    enabled: true,
    locked: true,
    position: 'center',
    size: 140,
    foreground_color: '#111111',
    background_color: '#ffffff',
    margin: 4,
    opacity: 1,
    rotation: 0,
  },
  three_d: {
    enabled: false,
    surface_finish: 'glossy',
    base_style: 'classic',
    thickness: 10,
    stand_angle: 8,
    auto_rotate: false,
    design_variant: 'editorial',
    qr_frame: 'outlined',
    accent_mode: 'rail',
  },
};

const clamp = (value, min, max) =>
  Math.min(max, Math.max(min, Number(value) || min));

const clonePlain = (value) => {
  if (value === undefined || value === null) {
    return value;
  }

  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

const getAiSelectedOptions = (
  aiConfiguration = {},
  aiSelectedOptions = {},
) => {
  const selectedOptions =
    aiSelectedOptions ||
    aiConfiguration?.selected_options ||
    aiConfiguration?.selectedOptions ||
    {};

  return {
    size:
      selectedOptions?.size ||
      aiConfiguration?.selected_size ||
      aiConfiguration?.selected_product_size ||
      aiConfiguration?.size ||
      '',

    material:
      selectedOptions?.material ||
      aiConfiguration?.selected_material ||
      aiConfiguration?.selected_product_material ||
      aiConfiguration?.material ||
      '',

    color:
      selectedOptions?.color ||
      aiConfiguration?.selected_color ||
      aiConfiguration?.selected_product_color ||
      aiConfiguration?.product_color ||
      aiConfiguration?.accent_color ||
      '',

    previewMode:
      selectedOptions?.preview_mode ||
      selectedOptions?.previewMode ||
      aiConfiguration?.preview_mode ||
      aiConfiguration?.previewMode ||
      '',
  };
};

const normalizePreviewMode = (value) => {
  if (value === '3d' || value === '2d') {
    return value;
  }

  return '';
};

const getPreviewModeForDesigner = ({
  aiConfiguration = null,
  aiSelectedOptions = null,
  product = null,
  fallback = '2d',
}) => {
  const selectedOptions =
    aiSelectedOptions ||
    aiConfiguration?.selected_options ||
    aiConfiguration?.selectedOptions ||
    {};

  const explicit =
    normalizePreviewMode(aiConfiguration?.preview_mode) ||
    normalizePreviewMode(aiConfiguration?.previewMode) ||
    normalizePreviewMode(selectedOptions?.preview_mode) ||
    normalizePreviewMode(selectedOptions?.previewMode);

  if (explicit) {
    return explicit;
  }

  if (
    aiConfiguration?.three_d?.enabled === true ||
    aiConfiguration?.three_d?.enabled === 1 ||
    aiConfiguration?.three_d?.enabled === '1'
  ) {
    return '3d';
  }

  if (
    aiConfiguration?.accepted_from_ai &&
    product?.preview_mode !== '2d'
  ) {
    return '3d';
  }

  if (product?.preview_mode === '3d') {
    return '3d';
  }

  if (product?.preview_mode === '2d') {
    return '2d';
  }

  return fallback === '3d' ? '3d' : '2d';
};

const normalizeAiConfigurationForDesigner = (
  aiConfiguration = {},
  previewMode = '2d',
) => {
  const nextConfiguration = clonePlain(aiConfiguration) || {};

  return {
    ...nextConfiguration,
    preview_mode: previewMode,

    restaurant_logo:
      nextConfiguration.restaurant_logo ??
      DEFAULT_CONFIGURATION.restaurant_logo,

    restaurant_name:
      nextConfiguration.restaurant_name ??
      DEFAULT_CONFIGURATION.restaurant_name,

    custom_text:
      nextConfiguration.custom_text ??
      DEFAULT_CONFIGURATION.custom_text,

    background_color:
      nextConfiguration.background_color ??
      DEFAULT_CONFIGURATION.background_color,

    text_color:
      nextConfiguration.text_color ??
      DEFAULT_CONFIGURATION.text_color,

    accent_color:
      nextConfiguration.accent_color ??
      DEFAULT_CONFIGURATION.accent_color,

    editor_elements:
      nextConfiguration.editor_elements || {},

    qr: {
      ...DEFAULT_CONFIGURATION.qr,
      ...(nextConfiguration.qr || {}),
      enabled: true,
      locked: true,
    },

    three_d: {
      ...DEFAULT_CONFIGURATION.three_d,
      ...(nextConfiguration.three_d || {}),
      enabled: previewMode === '3d',
    },
  };
};

function DesignerSelect({
  value,
  onChange,
  options,
  placeholder = '',
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected =
    options.find(
      (option) =>
        String(option.value) ===
        String(value),
    ) || null;

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      'pointerdown',
      handlePointerDown,
    );

    return () =>
      document.removeEventListener(
        'pointerdown',
        handlePointerDown,
      );
  }, []);

  return (
    <div
      ref={rootRef}
      className={`qr-print-designer-select ${
        open ? 'is-open' : ''
      }`}
    >
      <button
        type="button"
        className="qr-print-designer-select__trigger"
        onClick={() =>
          setOpen((current) => !current)
        }
        aria-expanded={open}
      >
        <span>
          {selected?.label || placeholder}
        </span>

        <ChevronDown
          size={15}
          className="qr-print-designer-select__chevron"
        />
      </button>

      {open ? (
        <div className="qr-print-designer-select__menu">
          {options.map((option) => {
            const isSelected =
              String(option.value) ===
              String(value);

            return (
              <button
                type="button"
                key={String(option.value)}
                className={`qr-print-designer-select__option ${
                  isSelected
                    ? 'is-selected'
                    : ''
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span>{option.label}</span>

                {isSelected ? (
                  <Check size={14} />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function QrPrintDesigner() {
  const { t } = useLanguage();
  const { user, getFeature } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialAiStateRef = useRef(location.state || null);
  const { productId } = useParams();
  const previewRef = useRef(null);
  const [previewFullscreen, setPreviewFullscreen] =
    useState(false);
  const [selected2DBlockId, setSelected2DBlockId] = useState(null);
  const [previewZoom, setPreviewZoom] = useState(100);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const recommendationCustomizationId =
    params.get('customizationId') || '';
  // Present only when customizing a product already attached to an order.
  const orderId = params.get('orderId');
  const orderItemId = params.get('orderItemId');
  const editingOrderItem = Boolean(orderId && orderItemId);
  const [orderDesignContext, setOrderDesignContext] = useState(null);
  const [orderDesignError, setOrderDesignError] = useState('');

  const [product, setProduct] = useState(null);
  const [menus, setMenus] = useState([]);

  const [menuId, setMenuId] = useState(() =>
    editingOrderItem ? '' : (params.get('menuId') || ''),
  );

  // For existing orders, never use the admin's cached restaurant/menu.
  const establishmentId = editingOrderItem
    ? String(orderDesignContext?.order?.establishment_id ?? '')
    : String(
        params.get('establishmentId') ||
        getUserEstablishmentId(user) ||
        '',
      );

  useEffect(() => {
    if (!editingOrderItem) return undefined;
    let active = true;
    setOrderDesignContext(null);
    setOrderDesignError('');
    setMenuId('');

    qrPrintApi.getOrder(orderId).then(({ data }) => {
      if (!active) return;
      const order = data?.data;
      const item = order?.items?.find((entry) => String(entry.id) === String(orderItemId));
      if (!item || String(item.qr_print_product_id ?? item.product?.id) !== String(productId)) {
        setOrderDesignError('The selected order item does not match this product.');
        return;
      }
      if (!['quotation_requested', 'negotiating', 'price_accepted', 'pending', 'awaiting_design'].includes(order.status)) {
        setOrderDesignError('The order is no longer awaiting design.');
        return;
      }
      if (!order.establishment_id) {
        setOrderDesignError('This order has no customer establishment.');
        return;
      }
      setOrderDesignContext({ item, order });
    }).catch((error) => {
      if (active) setOrderDesignError(error?.response?.data?.message || 'Unable to load this order item.');
    });
    return () => { active = false; };
  }, [editingOrderItem, orderId, orderItemId, productId]);

  const qrAiFeature = getFeature(
    'qr_print_ai_design',
  );

  const qrAiVisible =
    qrAiFeature?.visible === true;

  const qrAiAvailable =
    qrAiVisible &&
    qrAiFeature?.available === true &&
    qrAiFeature?.locked !== true;

  const [previewMode, setPreviewMode] = useState('2d');
  const [activeDesignSide, setActiveDesignSide] = useState('front');
  const [configuration, setConfiguration] = useState(
    DEFAULT_CONFIGURATION,
  );
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [customerNote, setCustomerNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [
    loadingRecommendationCustomization,
    setLUutqWQYDswqXG4LzJaCd8krpKAykq1LEY,
  ] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      const response = await qrPrintApi.getProduct(productId);
      const nextProduct = response.data?.data;

      setProduct(nextProduct);
      setActiveDesignSide('front');

      if (nextProduct?.editor_schema?.blocks?.length) {
        const defaultsBySide = { front: {}, back: {} };

        nextProduct.editor_schema.blocks.forEach((block) => {
          if (block?.enabled !== false && block?.id) {
            const side = block.side === 'back' ? 'back' : 'front';
            defaultsBySide[side][block.id] =
              block.default === undefined
                ? {}
                : JSON.parse(JSON.stringify(block.default));
          }
        });

        setConfiguration((current) => ({
          ...current,
          editor_elements: {
            ...defaultsBySide.front,
            ...(current.editor_elements || {}),
          },
          sides: {
            front: {
              ...(current.sides?.front || {}),
              editor_elements: {
                ...defaultsBySide.front,
                ...(current.sides?.front?.editor_elements || {}),
              },
            },
            ...(nextProduct?.supports_back_design ? {
              back: {
                ...(current.sides?.back || {}),
                editor_elements: {
                  ...defaultsBySide.back,
                  ...(current.sides?.back?.editor_elements || {}),
                },
              },
            } : {}),
          },
        }));
      }

      const initialAiConfiguration =
        initialAiStateRef.current?.aiConfiguration;

      const initialAiOptions = getAiSelectedOptions(
        initialAiConfiguration,
        initialAiStateRef.current?.aiSelectedOptions,
      );

      const initialMode = getPreviewModeForDesigner({
        aiConfiguration: initialAiConfiguration,
        aiSelectedOptions: initialAiStateRef.current?.aiSelectedOptions,
        product: nextProduct,
        fallback: nextProduct?.preview_mode === '2d' ? '2d' : '3d',
      });

      setPreviewMode(initialMode);

      setConfiguration((current) => ({
        ...current,
        preview_mode: initialMode,
        three_d: {
          ...current.three_d,
          ...(initialAiConfiguration?.three_d || {}),
          enabled: initialMode === '3d',
        },
      }));

      setQuantity(nextProduct?.minimum_quantity || 1);
      setSelectedSize(
        initialAiOptions.size ||
          getOptionValue(nextProduct?.available_sizes?.[0]) ||
          '',
      );
      setSelectedMaterial(
        initialAiOptions.material ||
          getOptionValue(
            nextProduct?.available_materials?.[0],
          ) ||
          '',
      );
      setSelectedColor(
        initialAiOptions.color ||
          getOptionValue(nextProduct?.available_colors?.[0]) ||
          '',
      );
    };

    loadProduct();
  }, [productId]);

  useEffect(() => {
    let active = true;
    if (!establishmentId) {
      setMenus([]);
      setMenuId('');
      return undefined;
    }

    const loadMenus = async () => {
      try {
        const response = await qrPrintApi.getMenus(establishmentId, { per_page: 100 });
        if (!active) return;
        const payload = response?.data?.data;
        const available = payload?.menus ?? payload?.data ?? payload ?? [];
        const nextMenus = Array.isArray(available) ? available.filter((candidate) =>
          !candidate.establishment_id || String(candidate.establishment_id) === String(establishmentId)
        ) : [];
        setMenus(nextMenus);

        if (editingOrderItem) {
          const linkedMenuId = String(
            orderDesignContext?.item?.design?.menu_id ??
            orderDesignContext?.item?.design?.menu?.id ?? '',
          );
          // Never replace an order's linked menu with an arbitrary default menu.
          const linkedMenu = nextMenus.find((candidate) => String(candidate.id) === linkedMenuId);
          if (!linkedMenu) {
            setMenuId('');
            setOrderDesignError(
              `Order item #${orderItemId} links to menu #${linkedMenuId || 'missing'}, ` +
              `which is not available for customer establishment #${establishmentId}. ` +
              'Check the Field Sales menu association before saving the design.',
            );
          } else {
            setMenuId(String(linkedMenu.id));
            setOrderDesignError('');
          }
          return;
        }

        const requested = params.get('menuId') || '';
        const selected = nextMenus.find((candidate) => String(candidate.id) === requested) ||
          nextMenus.find((candidate) => candidate.is_default === true || candidate.is_default === 1) ||
          nextMenus[0];
        setMenuId(selected?.id ? String(selected.id) : '');
      } catch (error) {
        if (!active) return;
        console.error('Failed to load QR Print menus:', error);
        setMenus([]);
        setMenuId('');
        if (editingOrderItem) {
          setOrderDesignError(error?.response?.data?.message || 'Unable to load menus for this order.');
        }
      }
    };

    loadMenus();
    return () => { active = false; };
  }, [establishmentId, editingOrderItem, orderDesignContext, orderItemId, params]);

  const menu = menus.find(
    (item) => String(item.id) === String(menuId),
  );

  const menuUrl = menu?.slug
    ? `${window.location.origin}/menu/${menu.slug}`
    : window.location.origin;

  const productPreviewMode = product?.preview_mode || 'both';

  const editorSchema = useMemo(
    () =>
      product?.editor_schema
        ? normalizeQrEditorSchema(product.editor_schema)
        : null,
    [product?.editor_schema],
  );

  const hasEditorSchema = Boolean(
    editorSchema?.blocks?.length,
  );

  const supportsQr = ['qr', 'qr_nfc'].includes(product?.access_type || 'qr');
  const supportsNfc = ['nfc', 'qr_nfc'].includes(product?.access_type);
  const supportsBackDesign = product?.supports_back_design === true;

  const activeSideConfiguration = useMemo(() => {
    const sideConfig = configuration?.sides?.[activeDesignSide];
    return sideConfig ? { ...configuration, ...sideConfig } : configuration;
  }, [configuration, activeDesignSide]);

  const updateActiveSideConfiguration = (updater) => {
    setConfiguration((previous) => {
      const currentSide = previous?.sides?.[activeDesignSide] || {};
      const mergedCurrent = { ...previous, ...currentSide };
      const nextMerged = typeof updater === 'function' ? updater(mergedCurrent) : updater;
      const sideOnly = {
        editor_elements: nextMerged?.editor_elements || {},
        background_color: nextMerged?.background_color ?? previous.background_color,
        text_color: nextMerged?.text_color ?? previous.text_color,
        accent_color: nextMerged?.accent_color ?? previous.accent_color,
        restaurant_name: nextMerged?.restaurant_name ?? previous.restaurant_name,
        custom_text: nextMerged?.custom_text ?? previous.custom_text,
        qr: nextMerged?.qr || previous.qr,
      };

      return {
        ...previous,
        sides: {
          ...(previous.sides || {}),
          [activeDesignSide]: sideOnly,
        },
      };
    });
  };


  const getActiveDesignerConfiguration = () => activeSideConfiguration;

  const updateActive2DElement = (blockId, patch) => {
    updateActiveSideConfiguration((current) => {
      const block = getBlocksForDesignerSide(editorSchema, activeDesignSide).find(
        (item) => item.id === blockId,
      );
      const fallbackValue = block ? getDesignerBlockValue(current, block) : {};
      const currentValue = current.editor_elements?.[blockId];

      return {
        ...current,
        editor_elements: {
          ...(current.editor_elements || {}),
          [blockId]: {
            ...(typeof fallbackValue === 'object' && fallbackValue !== null
              ? fallbackValue
              : {}),
            ...(typeof currentValue === 'object' && currentValue !== null
              ? currentValue
              : {}),
            ...patch,
          },
        },
      };
    });
  };

  const replaceActive2DElement = (blockId, value) => {
    updateActiveSideConfiguration((current) => ({
      ...current,
      editor_elements: {
        ...(current.editor_elements || {}),
        [blockId]: value,
      },
    }));
  };

  const updateActive2DCanvasSize = (patch) => {
    setConfiguration((current) => {
      const sideConfig = current?.sides?.[activeDesignSide] || {};
      const sideCanvas = sideConfig.canvas || current.canvas || {};
      const currentSize = getDesignerPrintSizeCm({
        schema: editorSchema,
        configuration: { ...current, ...sideConfig },
        product,
        selectedSize,
      });

      return {
        ...current,
        sides: {
          ...(current.sides || {}),
          [activeDesignSide]: {
            ...sideConfig,
            canvas: {
              ...sideCanvas,
              width_cm: clamp(
                patch.width_cm ?? currentSize.width,
                PRINT_SIZE_MIN_CM,
                PRINT_SIZE_MAX_CM,
              ),
              height_cm: clamp(
                patch.height_cm ?? currentSize.height,
                PRINT_SIZE_MIN_CM,
                PRINT_SIZE_MAX_CM,
              ),
            },
          },
        },
      };
    });
  };

  const changeSelectedProductSize = (value) => {
    setSelectedSize(value);

    const parsedSize = getSelectedProductSizeCm(product, value);

    if (parsedSize) {
      updateActive2DCanvasSize({
        width_cm: parsedSize.width,
        height_cm: parsedSize.height,
      });
    }
  };

  const addActive2DBlock = (type) => {
    const block = makeDesignerBlock(type);

    setConfiguration((current) => ({
      ...current,
      extra_2d_blocks: [
        ...(current.extra_2d_blocks || []),
        {
          ...block,
          side: activeDesignSide,
        },
      ],
    }));

    setSelected2DBlockId(block.id);
  };

  const removeActive2DBlock = (blockId) => {
    const block = [
      ...getBlocksForDesignerSide(editorSchema, activeDesignSide),
      ...(configuration.extra_2d_blocks || []),
    ].find((item) => item.id === blockId);

    if (block?.required && !['shape', 'image', 'nfc'].includes(block.type)) {
      return;
    }

    setConfiguration((current) => {
      const nextElements = { ...(current.editor_elements || {}) };
      delete nextElements[blockId];

      return {
        ...current,
        editor_elements: nextElements,
        extra_2d_blocks: (current.extra_2d_blocks || []).filter(
          (item) => item.id !== blockId,
        ),
      };
    });

    setSelected2DBlockId(null);
  };

  const active2DPrintSizeCm = getDesignerPrintSizeCm({
    schema: editorSchema,
    configuration: activeSideConfiguration,
    product,
    selectedSize,
  });

  const supports2D =
    productPreviewMode === '2d' ||
    productPreviewMode === 'both';

  const supports3D =
    productPreviewMode === '3d' ||
    productPreviewMode === 'both';

  const updateConfiguration = (key, value) => {
    setConfiguration((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const updateQr = (key, value) => {
    setConfiguration((current) => ({
      ...current,
      qr: {
        ...current.qr,
        [key]: value,
      },
    }));
  };

  const updateThreeD = (key, value) => {
    setConfiguration((current) => ({
      ...current,
      three_d: {
        ...current.three_d,
        [key]: value,
      },
    }));
  };

  const changePreviewMode = (mode) => {
    setPreviewMode(mode);

    setConfiguration((current) => ({
      ...current,
      preview_mode: mode,
      three_d: {
        ...current.three_d,
        enabled: mode === '3d',
      },
    }));
  };

  useEffect(() => {
    const aiConfiguration = location.state?.aiConfiguration;

    if (!aiConfiguration) {
      return;
    }

    const aiOptions = getAiSelectedOptions(
      aiConfiguration,
      location.state?.aiSelectedOptions,
    );

    const nextPreviewMode = getPreviewModeForDesigner({
      aiConfiguration: {
        ...aiConfiguration,
        preview_mode:
          location.state?.aiPreviewMode ||
          aiConfiguration.preview_mode,
      },
      aiSelectedOptions: location.state?.aiSelectedOptions,
      product,
      fallback: previewMode,
    });

    setPreviewMode(nextPreviewMode);

    setConfiguration((current) => ({
      ...current,
      ...normalizeAiConfigurationForDesigner(
        aiConfiguration,
        nextPreviewMode,
      ),
      qr: {
        ...current.qr,
        ...(aiConfiguration.qr || {}),
        enabled: true,
        locked: true,
        rotation: 0,
        opacity: 1,
      },
      three_d: {
        ...current.three_d,
        ...(aiConfiguration.three_d || {}),
        enabled: nextPreviewMode === '3d',
      },
    }));

    if (aiOptions.size) {
      setSelectedSize(aiOptions.size);
    }

    if (aiOptions.material) {
      setSelectedMaterial(aiOptions.material);
    }

    if (aiOptions.color) {
      setSelectedColor(aiOptions.color);
    }

    const cleanSearch = new URLSearchParams(location.search);

    cleanSearch.delete('customizationId');

    navigate(
      `${location.pathname}${
        cleanSearch.toString()
          ? `?${cleanSearch.toString()}`
          : ''
      }`,
      {
        replace: true,
        state: null,
      },
    );
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
    previewMode,
    product,
  ]);

  const openAiCustomizer = () => {
    if (
      !qrAiAvailable ||
      !product?.id ||
      !menu?.id ||
      !establishmentId
    ) {
      return;
    }

    const query = new URLSearchParams({
      menuId: String(menu.id),
      establishmentId: String(establishmentId),
    });

    navigate(
      `/qr-print/products/${product.id}/ai-customize?${query.toString()}`,
    );
  };

  useEffect(() => {
    if (
      !recommendationCustomizationId ||
      location.state?.aiConfiguration
    ) {
      return;
    }

    let active = true;

    const loadRecommendationCustomization = async () => {
      setLUutqWQYDswqXG4LzJaCd8krpKAykq1LEY(true);

      try {
        const response =
          await qrPrintApi.getAiCustomization(
            recommendationCustomizationId,
          );

        if (!active) {
          return;
        }

        const aiCustomization =
          response.data?.data;

        const aiConfiguration =
          aiCustomization?.configuration;

        if (!aiConfiguration) {
          return;
        }

        const aiOptions = getAiSelectedOptions(
          aiConfiguration,
          aiCustomization?.selected_options,
        );

        const nextPreviewMode = getPreviewModeForDesigner({
          aiConfiguration,
          aiSelectedOptions: aiCustomization?.selected_options,
          product,
          fallback: previewMode,
        });

        setPreviewMode(nextPreviewMode);

        setConfiguration((current) => ({
          ...current,
          ...normalizeAiConfigurationForDesigner(
            aiConfiguration,
            nextPreviewMode,
          ),
          qr: {
            ...current.qr,
            ...(aiConfiguration.qr || {}),
            enabled: true,
            locked: true,
            rotation: 0,
            opacity: 1,
          },
          three_d: {
            ...current.three_d,
            ...(aiConfiguration.three_d || {}),
            enabled: nextPreviewMode === '3d',
          },
        }));

        if (aiOptions.size) {
          setSelectedSize(aiOptions.size);
        }

        if (aiOptions.material) {
          setSelectedMaterial(aiOptions.material);
        }

        if (aiOptions.color) {
          setSelectedColor(aiOptions.color);
        }
      } catch (error) {
        console.error(
          'Failed to load recommended AI customization:',
          error,
        );
      } finally {
        if (active) {
          setLUutqWQYDswqXG4LzJaCd8krpKAykq1LEY(false);
        }
      }
    };

    loadRecommendationCustomization();

    return () => {
      active = false;
    };
  }, [
    location.state,
    product,
    recommendationCustomizationId,
    previewMode,
  ]);

  useEffect(() => {
    if (!hasEditorSchema) {
      return;
    }

    const elements =
      configuration.editor_elements || {};

    const restaurantBlock =
      editorSchema.blocks.find(
        (block) =>
          block.id === 'restaurant-name' ||
          (
            block.type === 'text' &&
            /restaurant/i.test(block.label || '')
          ),
      );

    const callToActionBlock =
      editorSchema.blocks.find(
        (block) =>
          block.id === 'call-to-action' ||
          (
            block.type === 'text' &&
            block.id !== restaurantBlock?.id
          ),
      );

    const qrBlock =
      editorSchema.blocks.find(
        (block) => block.type === 'qr',
      );

    const backgroundBlock =
      editorSchema.blocks.find(
        (block) =>
          block.type === 'background_color',
      );

    const finishBlock =
      editorSchema.blocks.find(
        (block) =>
          block.type === 'surface_finish',
      );

    const propertyBlocks =
      editorSchema.blocks.filter(
        (block) =>
          block.type === 'three_d_property',
      );

    setConfiguration((current) => {
      const next = {
        ...current,
        three_d: {
          ...current.three_d,
        },
      };

      if (restaurantBlock) {
        const value =
          elements[restaurantBlock.id] ??
          restaurantBlock.default;

        if (
          value &&
          typeof value === 'object' &&
          value.text !== undefined
        ) {
          next.restaurant_name = value.text;
        }
      }

      if (callToActionBlock) {
        const value =
          elements[callToActionBlock.id] ??
          callToActionBlock.default;

        if (
          value &&
          typeof value === 'object' &&
          value.text !== undefined
        ) {
          next.custom_text = value.text;
        }
      }

      if (backgroundBlock) {
        const value =
          elements[backgroundBlock.id] ??
          backgroundBlock.default;

        if (typeof value === 'string' && value) {
          next.background_color = value;
        }
      }

      if (qrBlock) {
        const value =
          elements[qrBlock.id] ??
          qrBlock.default;

        if (value && typeof value === 'object') {
          next.qr = {
            ...current.qr,
            size:
              value.size !== undefined
                ? Math.round(
                    96 +
                      (
                        Number(value.size) /
                        100
                      ) *
                        220,
                  )
                : current.qr?.size,
            foreground_color:
              value.foreground_color ||
              current.qr?.foreground_color,
            background_color:
              value.background_color ||
              current.qr?.background_color,
          };
        }
      }

      if (finishBlock) {
        next.three_d.surface_finish =
          elements[finishBlock.id] ??
          finishBlock.default ??
          current.three_d?.surface_finish;
      }

      propertyBlocks.forEach((block) => {
        const property =
          block.settings?.property;

        if (!property) {
          return;
        }

        const value =
          elements[block.id] ??
          block.default;

        if (value !== undefined) {
          next.three_d[property] = value;
        }
      });

      return next;
    });
  }, [
    configuration.editor_elements,
    editorSchema,
    hasEditorSchema,
  ]);

  const selectedColorHex = useMemo(() => {
    if (
      configuration.accepted_from_ai &&
      /^#[0-9a-f]{6}$/i.test(
        configuration.selected_product_color ||
          configuration.accent_color ||
          '',
      )
    ) {
      return (
        configuration.selected_product_color ||
        configuration.accent_color
      );
    }

    const option = product?.available_colors?.find(
      (item) => getOptionValue(item) === selectedColor,
    );

    if (
      option &&
      typeof option === 'object' &&
      option.hex
    ) {
      return option.hex;
    }

    if (/^#[0-9a-f]{6}$/i.test(selectedColor)) {
      return selectedColor;
    }

    return configuration.accent_color;
  }, [
    configuration.accepted_from_ai,
    configuration.accent_color,
    configuration.selected_product_color,
    product?.available_colors,
    selectedColor,
  ]);

  const updateThreeDConfiguration = (patch) => {
    setConfiguration((current) => ({
      ...current,
      three_d: {
        ...(current.three_d || {}),
        ...patch,
      },
    }));
  };

  const menuOptions = useMemo(
    () => [
      {
        value: '',
        label: translateFallback(
          t,
          'qrPrint.designer.selectMenu',
          'Select a menu',
        ),
      },
      ...menus.map((item) => ({
        value: String(item.id),
        label: item.name,
      })),
    ],
    [menus, t],
  );

  const sizeOptions = useMemo(
    () =>
      (product?.available_sizes || []).map(
        (option) => ({
          value: getOptionValue(option),
          label: getOptionLabel(option),
        }),
      ),
    [product?.available_sizes],
  );

  const materialOptions = useMemo(
    () =>
      (
        product?.available_materials || []
      ).map((option) => ({
        value: getOptionValue(option),
        label: getOptionLabel(option),
      })),
    [product?.available_materials],
  );

  const colorOptions = useMemo(
    () =>
      (product?.available_colors || []).map(
        (option) => ({
          value: getOptionValue(option),
          label: getOptionLabel(option),
        }),
      ),
    [product?.available_colors],
  );

  useEffect(() => {
    const handleFullscreenChange = () => {
      setPreviewFullscreen(
        document.fullscreenElement === previewRef.current,
      );
    };

    document.addEventListener(
      'fullscreenchange',
      handleFullscreenChange,
    );

    return () => {
      document.removeEventListener(
        'fullscreenchange',
        handleFullscreenChange,
      );
    };
  }, []);

  const togglePreviewFullscreen = async () => {
    const element = previewRef.current;

    if (!element) {
      return;
    }

    try {
      if (document.fullscreenElement === element) {
        await document.exitFullscreen();
      } else {
        await element.requestFullscreen();
      }
    } catch (error) {
      console.error(
        'Unable to toggle preview fullscreen:',
        error,
      );
    }
  };

  const saveAndAddToCart = async () => {
    if (!product || !menuId || !establishmentId || (editingOrderItem && (!orderDesignContext || orderDesignError))) return;

    setSaving(true);

    try {
      const safeQrSize = clamp(
        configuration.qr.size,
        96,
        420,
      );

      const designConfiguration = {
        ...configuration,
        ...(editingOrderItem ? { design_status: 'customized', needs_customer_design: false } : {}),
        preview_mode: previewMode,
        selected_product_color: selectedColorHex,
        qr: {
          ...configuration.qr,
          enabled: supportsQr,
          locked: true,
          size: safeQrSize,
          margin: clamp(configuration.qr.margin, 4, 20),
          rotation: 0,
          opacity: 1,
          foreground: configuration.qr.foreground_color,
          background: configuration.qr.background_color,
        },
        three_d: {
          ...configuration.three_d,
          enabled: previewMode === '3d',
        },
      };

      const designResponse =
        await qrPrintApi.createDesign({
          establishment_id: Number(establishmentId),
          menu_id: Number(menuId),
          qr_print_product_id: Number(product.id),
          name: `${product.name} - ${menu?.name || 'Menu'}`,
          configuration: designConfiguration,
          status: 'ready',
          ...(editingOrderItem ? { order_item_id: Number(orderItemId) } : {}),
        });

      const design = designResponse.data?.data;

      if (editingOrderItem) {
        navigate(`/qr-print/orders/${orderId}`, { replace: true });
        return;
      }

      await qrPrintApi.addToCart({
        qr_print_design_id: design.id,
        quantity: Number(quantity),
        selected_options: {
          size: selectedSize || null,
          material: selectedMaterial || null,
          color: selectedColor || null,
          preview_mode: previewMode,
          surface_finish:
            configuration.three_d.surface_finish || null,
          base_style:
            configuration.three_d.base_style || null,
        },
        customer_note: customerNote || null,
      });

      navigate('/qr-print/cart');
    } finally {
      setSaving(false);
    }
  };

  if (!product) {
    return (
      <div className="qr-print-loading-block">
        <span className="qr-print-spinner" />
      </div>
    );
  }

  return (
    <main className="qr-print-page qr-print-designer-page">
      {orderDesignError && <p role="alert" style={{ color: '#991b1b' }}>{orderDesignError}</p>}
      {editingOrderItem && !orderDesignError && <p role="status" style={{ color: '#92400e' }}>Editing design for existing order #{orderId}. Saving will update the order, not add items to the cart.</p>}
      <header className="qr-print-page-header">
        <div className="qr-print-designer-header__identity">
          <Link
            to="/qr-print/products"
            className="qr-print-back-link"
          >
            <ArrowLeft size={16} />

            {translateFallback(
              t,
              'qrPrint.designer.back',
              'Back to products',
            )}
          </Link>

          <h1>
            {translateFallback(
              t,
              'qrPrint.designer.title',
              'Customize your QR product',
            )}
          </h1>

          <p>{product.name}</p>
        </div>

        <div className="qr-print-designer-header__actions">
          {supports2D && supports3D ? (
            <div className="qr-print-preview-mode-switch">
              <button
                type="button"
                className={
                  previewMode === '2d'
                    ? 'is-active'
                    : ''
                }
                onClick={() =>
                  changePreviewMode('2d')
                }
              >
                <Monitor size={18} />

                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.mode2d',
                    '2D preview',
                  )}
                </span>
              </button>

              <button
                type="button"
                className={
                  previewMode === '3d'
                    ? 'is-active'
                    : ''
                }
                onClick={() =>
                  changePreviewMode('3d')
                }
              >
                <Box size={18} />

                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.mode3d',
                    '3D preview',
                  )}
                </span>
              </button>
            </div>
          ) : null}

          <button
            type="button"
            className="qr-print-ai-style-button"
            disabled={!qrAiAvailable}
            onClick={openAiCustomizer}
            title={
              qrAiAvailable
                ? ''
                : translateFallback(
                    t,
                    'qrPrint.designer.aiLocked',
                    'AI product designer is disabled for this account.',
                  )
            }
          >
            <Sparkles size={18} />

            <span>
              {translateFallback(
                t,
                'qrPrint.designer.styleWithAi',
                'Customize with AI',
              )}
            </span>
          </button>
        </div>
      </header>

      <div className="qr-print-designer-grid qr-print-designer-workspace">
        <section
          className={`qr-print-designer-controls ${
            hasEditorSchema ? 'is-schema-driven' : ''
          }`}
        >
          {supportsBackDesign ? (
            <div className="qr-print-side-switcher">
              <span>Design side</span>
              <div>
                <button
                  type="button"
                  className={activeDesignSide === 'front' ? 'is-active' : ''}
                  onClick={() => setActiveDesignSide('front')}
                >Front</button>
                <button
                  type="button"
                  className={activeDesignSide === 'back' ? 'is-active' : ''}
                  onClick={() => setActiveDesignSide('back')}
                >Back</button>
              </div>
            </div>
          ) : null}

          {hasEditorSchema && previewMode === '2d' ? (
            <QrPrintCustomer2DControls
              schema={editorSchema}
              configuration={activeSideConfiguration}
              product={product}
              selectedBlockId={selected2DBlockId}
              onSelectBlock={setSelected2DBlockId}
              onElementChange={updateActive2DElement}
              onScalarElementChange={replaceActive2DElement}
              onAddBlock={addActive2DBlock}
              onRemoveBlock={removeActive2DBlock}
              printSizeCm={active2DPrintSizeCm}
              onCanvasSizeChange={updateActive2DCanvasSize}
              previewZoom={previewZoom}
              onPreviewZoomChange={setPreviewZoom}
              selectedSize={selectedSize}
              onSizeChange={changeSelectedProductSize}
              selectedMaterial={selectedMaterial}
              onMaterialChange={setSelectedMaterial}
              selectedColor={selectedColor}
              onColorChange={setSelectedColor}
              side={activeDesignSide}
            />
          ) : null}

          {hasEditorSchema && previewMode === '3d' ? (
            <QrDynamicCustomizationPanel
              schema={editorSchema}
              configuration={activeSideConfiguration}
              onConfigurationChange={updateActiveSideConfiguration}
              product={product}
              selectedSize={selectedSize}
              onSizeChange={setSelectedSize}
              selectedMaterial={selectedMaterial}
              onMaterialChange={setSelectedMaterial}
              selectedColor={selectedColor}
              onColorChange={setSelectedColor}
              threeDConfiguration={configuration.three_d}
              onThreeDChange={updateThreeDConfiguration}
              side={activeDesignSide}
            />
          ) : null}

          {recommendationCustomizationId ? (
            <div className="qr-print-ai-applied-banner">
              <Sparkles size={16} />

              <div>
                <strong>
                  AI recommendation loaded
                </strong>

                <span>
                  This is the same design shown in the recommendation popup.
                  You can edit it normally before adding it to your cart.
                </span>
              </div>
            </div>
          ) : null}

          <div className="qr-print-form-card">
            <h2>
              {translateFallback(
                t,
                'qrPrint.designer.menuTitle',
                'Menu & QR code',
              )}
            </h2>

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.menu',
                  'Menu',
                )}
              </span>

              <DesignerSelect
                value={menuId}
                onChange={(value) => {
                  if (!editingOrderItem) setMenuId(String(value));
                }}
                options={menuOptions}
                placeholder={translateFallback(
                  t,
                  'qrPrint.designer.selectMenu',
                  'Select a menu',
                )}
              />
            </label>

            <div className="qr-print-locked-note">
              <Lock size={18} />

              <div>
                <strong>
                  {translateFallback(
                    t,
                    'qrPrint.designer.qrLocked',
                    'QR code protected',
                  )}
                </strong>

                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.qrLockedDescription',
                    'The QR code cannot be removed and remains scannable.',
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="qr-print-form-card qr-print-legacy-content-card">
            <h2>
              {translateFallback(
                t,
                'qrPrint.designer.contentTitle',
                'Content',
              )}
            </h2>

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.restaurantName',
                  'Restaurant name',
                )}
              </span>

              <input
                value={configuration.restaurant_name}
                onChange={(event) =>
                  updateConfiguration(
                    'restaurant_name',
                    event.target.value,
                  )
                }
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.customText',
                  'Custom text',
                )}
              </span>

              <input
                value={configuration.custom_text}
                onChange={(event) =>
                  updateConfiguration(
                    'custom_text',
                    event.target.value,
                  )
                }
              />
            </label>

            <div className="qr-print-color-controls">
              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.background',
                    'Background',
                  )}
                </span>

                <input
                  type="color"
                  value={configuration.background_color}
                  onChange={(event) =>
                    updateConfiguration(
                      'background_color',
                      event.target.value,
                    )
                  }
                />
              </label>

              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.textColor',
                    'Text',
                  )}
                </span>

                <input
                  type="color"
                  value={configuration.text_color}
                  onChange={(event) =>
                    updateConfiguration(
                      'text_color',
                      event.target.value,
                    )
                  }
                />
              </label>

              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.accent',
                    'Accent',
                  )}
                </span>

                <input
                  type="color"
                  value={configuration.accent_color}
                  onChange={(event) =>
                    updateConfiguration(
                      'accent_color',
                      event.target.value,
                    )
                  }
                />
              </label>
            </div>
          </div>

          <div className="qr-print-form-card qr-print-legacy-product-options-card">
            <h2>
              {translateFallback(
                t,
                'qrPrint.designer.productOptions',
                'Product options',
              )}
            </h2>

            {product.available_sizes?.length > 0 ? (
              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.size',
                    'Size',
                  )}
                </span>

                <DesignerSelect
                  value={selectedSize}
                  onChange={setSelectedSize}
                  options={sizeOptions}
                />
              </label>
            ) : null}

            {product.available_materials?.length > 0 ? (
              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.material',
                    'Material',
                  )}
                </span>

                <DesignerSelect
                  value={selectedMaterial}
                  onChange={setSelectedMaterial}
                  options={materialOptions}
                />
              </label>
            ) : null}

            {product.available_colors?.length > 0 ? (
              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.color',
                    'Product color',
                  )}
                </span>

                <DesignerSelect
                  value={selectedColor}
                  onChange={setSelectedColor}
                  options={colorOptions}
                />
              </label>
            ) : null}

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.qrSize',
                  'QR size',
                )}{' '}
                — {configuration.qr.size}px
              </span>

              <input
                type="range"
                min="96"
                max="240"
                step="4"
                value={configuration.qr.size}
                onChange={(event) =>
                  updateQr(
                    'size',
                    Number(event.target.value),
                  )
                }
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.qrForeground',
                  'QR foreground',
                )}
              </span>

              <input
                type="color"
                value={configuration.qr.foreground_color}
                onChange={(event) =>
                  updateQr(
                    'foreground_color',
                    event.target.value,
                  )
                }
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.qrBackground',
                  'QR background',
                )}
              </span>

              <input
                type="color"
                value={configuration.qr.background_color}
                onChange={(event) =>
                  updateQr(
                    'background_color',
                    event.target.value,
                  )
                }
              />
            </label>
          </div>

          {previewMode === '3d' ? (
            <div className="qr-print-form-card qr-print-3d-options-card">
              <div className="qr-print-3d-card-heading">
                <div>
                  <span className="qr-print-3d-kicker">
                    <Box size={15} />
                    {translateFallback(
                      t,
                      'qrPrint.designer.threeDSettings',
                      '3D settings',
                    )}
                  </span>

                  <h2>
                    {translateFallback(
                      t,
                      'qrPrint.designer.threeDCustomization',
                      '3D product customization',
                    )}
                  </h2>
                </div>
              </div>

              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.surfaceFinish',
                    'Surface finish',
                  )}
                </span>

                <DesignerSelect
                  value={
                    configuration.three_d.surface_finish
                  }
                  onChange={(value) =>
                    updateThreeD(
                      'surface_finish',
                      value,
                    )
                  }
                  options={[
                    {
                      value: 'glossy',
                      label: translateFallback(
                        t,
                        'qrPrint.designer.glossy',
                        'Glossy',
                      ),
                    },
                    {
                      value: 'matte',
                      label: translateFallback(
                        t,
                        'qrPrint.designer.matte',
                        'Matte',
                      ),
                    },
                    {
                      value: 'frosted',
                      label: translateFallback(
                        t,
                        'qrPrint.designer.frosted',
                        'Frosted',
                      ),
                    },
                  ]}
                />
              </label>

              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.baseStyle',
                    'Base style',
                  )}
                </span>

                <DesignerSelect
                  value={
                    configuration.three_d.base_style
                  }
                  onChange={(value) =>
                    updateThreeD(
                      'base_style',
                      value,
                    )
                  }
                  options={[
                    {
                      value: 'classic',
                      label: translateFallback(
                        t,
                        'qrPrint.designer.baseClassic',
                        'Classic',
                      ),
                    },
                    {
                      value: 'wide',
                      label: translateFallback(
                        t,
                        'qrPrint.designer.baseWide',
                        'Wide',
                      ),
                    },
                    {
                      value: 'minimal',
                      label: translateFallback(
                        t,
                        'qrPrint.designer.baseMinimal',
                        'Minimal',
                      ),
                    },
                  ]}
                />
              </label>

              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.thickness',
                    'Thickness',
                  )}{' '}
                  — {configuration.three_d.thickness} mm
                </span>

                <input
                  type="range"
                  min="4"
                  max="22"
                  step="1"
                  value={configuration.three_d.thickness}
                  onChange={(event) =>
                    updateThreeD(
                      'thickness',
                      Number(event.target.value),
                    )
                  }
                />
              </label>

              <label>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.standAngle',
                    'Stand angle',
                  )}{' '}
                  — {configuration.three_d.stand_angle}°
                </span>

                <input
                  type="range"
                  min="0"
                  max="22"
                  step="1"
                  value={configuration.three_d.stand_angle}
                  onChange={(event) =>
                    updateThreeD(
                      'stand_angle',
                      Number(event.target.value),
                    )
                  }
                />
              </label>

              <label className="qr-print-3d-toggle-row">
                <input
                  type="checkbox"
                  checked={configuration.three_d.auto_rotate}
                  onChange={(event) =>
                    updateThreeD(
                      'auto_rotate',
                      event.target.checked,
                    )
                  }
                />

                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.autoRotate',
                    'Auto rotate 3D preview',
                  )}
                </span>
              </label>
            </div>
          ) : null}

          <div className="qr-print-form-card">
            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.quantity',
                  'Quantity',
                )}
              </span>

              <input
                type="number"
                min={product.minimum_quantity || 1}
                value={quantity}
                onChange={(event) =>
                  setQuantity(event.target.value)
                }
              />

              <small>
                {translateFallback(
                  t,
                  'qrPrint.designer.minimumQuantity',
                  'Minimum quantity: {{quantity}}',
                ).replace(
                  '{{quantity}}',
                  String(product.minimum_quantity || 1),
                )}
              </small>
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  'qrPrint.designer.note',
                  'Production note',
                )}
              </span>

              <textarea
                rows="3"
                value={customerNote}
                onChange={(event) =>
                  setCustomerNote(event.target.value)
                }
              />
            </label>
          </div>

          <section className="qr-print-order-section">
            <div className="qr-print-order-section__heading">
              <ShoppingCart size={17} />

              <div>
                <span>
                  {translateFallback(
                    t,
                    'qrPrint.designer.estimatedPrice',
                    'Estimated price',
                  )}
                </span>

                <strong>
                  {product.base_unit_price !== null
                    ? formatMoney(
                        Number(product.base_unit_price) *
                          Number(quantity || 0),
                      )
                    : translateFallback(
                        t,
                        'qrPrint.products.quoteOnly',
                        'Price on request',
                      )}
                </strong>
              </div>
            </div>

            {!editingOrderItem && <p>
              {translateFallback(
                t,
                'qrPrint.designer.negotiableHint',
                'This is only an estimate.',
              )}
            </p>}

            <button
              type="button"
              disabled={
                saving ||
                loadingRecommendationCustomization ||
                !menuId ||
                (editingOrderItem && (!orderDesignContext || Boolean(orderDesignError))) ||
                Number(quantity) <
                  Number(product.minimum_quantity || 1)
              }
              className="qr-print-button qr-print-button-primary qr-print-button-full"
              onClick={saveAndAddToCart}
            >
              {editingOrderItem ? <CheckCircle2 size={18} /> : <ShoppingCart size={18} />}

              {saving
                ? translateFallback(
                    t,
                    'qrPrint.designer.adding',
                    'Adding...',
                  )
                : translateFallback(
                    t,
                    'qrPrint.designer.addToCart',
                    editingOrderItem ? 'Save design to order' : 'Save design & add to cart',
                  )}
            </button>
          </section>
        </section>

        <aside
          ref={previewRef}
          className={`qr-print-designer-preview-column ${
            previewMode === '3d' ? 'is-3d' : 'is-2d'
          } ${
            previewFullscreen ? 'is-fullscreen' : ''
          }`}
        >
          <div className="qr-print-preview-header">
            <div>
              <strong>
                {previewMode === '3d'
                  ? translateFallback(
                      t,
                      'qrPrint.designer.live3dPreview',
                      'Live 3D preview',
                    )
                  : translateFallback(
                      t,
                      'qrPrint.designer.livePreview',
                      'Live 2D preview',
                    )}
              </strong>

              <small>
                {previewMode === '3d'
                  ? translateFallback(
                      t,
                      'qrPrint.designer.drag3dHint',
                      'Drag to rotate • scroll to zoom',
                    )
                  : translateFallback(
                      t,
                      'qrPrint.designer.scannable',
                      'Scannable QR',
                    )}
              </small>
            </div>

            <div className="qr-print-preview-header__actions">
              <div className="qr-print-smart-preview-badges">
                {supportsQr ? (
                  <span className="qr-print-scannable-badge">
                    <QrCode size={14} /> QR
                  </span>
                ) : null}
                {supportsNfc ? (
                  <span className="qr-print-scannable-badge">
                    <Nfc size={14} /> NFC
                  </span>
                ) : null}
                {supportsBackDesign ? (
                  <button
                    type="button"
                    className="qr-print-preview-flip-button"
                    onClick={() =>
                      setActiveDesignSide((side) => side === 'front' ? 'back' : 'front')
                    }
                  >
                    <RotateCcw size={13} />
                    {activeDesignSide === 'front' ? 'Show back' : 'Show front'}
                  </button>
                ) : null}
              </div>

              <button
                type="button"
                className="qr-print-preview-fullscreen-button"
                onClick={togglePreviewFullscreen}
                aria-label={
                  previewFullscreen
                    ? 'Exit fullscreen preview'
                    : 'Open fullscreen preview'
                }
                title={
                  previewFullscreen
                    ? 'Exit fullscreen'
                    : 'Fullscreen preview'
                }
              >
                {previewFullscreen ? (
                  <Minimize2 size={14} />
                ) : (
                  <Maximize2 size={14} />
                )}
              </button>
            </div>
          </div>

          <div className="qr-print-preview-stage">
            {previewMode === '3d' ? (
              <QrProduct3DViewer
                product={product}
                menuUrl={menuUrl}
                configuration={configuration}
                selectedMaterial={selectedMaterial}
                selectedSize={selectedSize}
                productColor={selectedColorHex}
                restaurantName={
                  configuration.restaurant_name ||
                  menu?.establishment?.name ||
                  'Restaurant'
                }
              />
            ) : (
              <div className="qr-print-product-mockup qr-print-product-mockup-design-only">
                <div className="qr-print-design-only-frame">
                  {hasEditorSchema ? (
                    <QrPrintCustomer2DPreview
                      schema={editorSchema}
                      configuration={activeSideConfiguration}
                      extraBlocks={configuration.extra_2d_blocks || []}
                      menuUrl={menuUrl}
                      fallbackRestaurantName={
                        activeSideConfiguration.restaurant_name ||
                        menu?.establishment?.name ||
                        'Restaurant'
                      }
                      productType={product?.product_type}
                      product={product}
                      selectedSize={selectedSize}
                      selectedBlockId={selected2DBlockId}
                      onSelectBlock={setSelected2DBlockId}
                      onElementChange={updateActive2DElement}
                      printSizeCm={active2DPrintSizeCm}
                      previewZoom={previewZoom}
                      side={activeDesignSide}
                    />
                  ) : (
                    <div
                      className="qr-print-design-surface"
                      style={{
                        backgroundColor:
                          configuration.background_color,
                        color: configuration.text_color,
                        '--qr-print-accent':
                          configuration.accent_color,
                      }}
                    >
                      <div className="qr-print-design-brand">
                        <span className="qr-print-design-logo">
                          {configuration.restaurant_logo
                            ? 'LOGO'
                            : ''}
                        </span>

                        <strong>
                          {configuration.restaurant_name ||
                            menu?.establishment?.name ||
                            'Restaurant'}
                        </strong>
                      </div>

                      <div className="qr-print-code-wrap">
                        <QRCodeSVG
                          value={menuUrl}
                          size={Math.min(
                            220,
                            Math.max(
                              120,
                              configuration.qr.size,
                            ),
                          )}
                          level="H"
                          marginSize={configuration.qr.margin}
                          bgColor={
                            configuration.qr.background_color
                          }
                          fgColor={
                            configuration.qr.foreground_color
                          }
                        />
                      </div>

                      <p>{configuration.custom_text}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}

function QrPrintCustomer2DControls({
  schema,
  configuration,
  product,
  selectedBlockId,
  onSelectBlock,
  onElementChange,
  onScalarElementChange,
  onAddBlock,
  onRemoveBlock,
  printSizeCm,
  onCanvasSizeChange,
  previewZoom,
  onPreviewZoomChange,
  selectedSize,
  onSizeChange,
  selectedMaterial,
  onMaterialChange,
  selectedColor,
  onColorChange,
  side = 'front',
}) {
  const baseBlocks = getBlocksForDesignerSide(schema, side);
  const extraBlocks = configuration?.extra_2d_blocks || [];
  const editableBlocks = [...baseBlocks, ...extraBlocks].filter((block) =>
    ['text', 'logo', 'image', 'qr', 'nfc', 'shape', 'background_color', 'product_color'].includes(block.type),
  );
  const selectedBlock =
    editableBlocks.find((block) => block.id === selectedBlockId) ||
    editableBlocks.find((block) => ['text', 'logo', 'image', 'qr', 'nfc', 'shape'].includes(block.type)) ||
    editableBlocks[0] ||
    null;
  const selectedValue = selectedBlock ? getDesignerBlockValue(configuration, selectedBlock) : {};

  useEffect(() => {
    if (!selectedBlockId && selectedBlock?.id) {
      onSelectBlock(selectedBlock.id);
    }
  }, [selectedBlock, selectedBlockId, onSelectBlock]);

  const productSizeOptions = (product?.available_sizes || []).map((option) => ({
    value: getOptionValue(option),
    label: getOptionLabel(option),
  }));

  const materialOptions = (product?.available_materials || []).map((option) => ({
    value: getOptionValue(option),
    label: getOptionLabel(option),
  }));

  const colorOptions = (product?.available_colors || []).map((option) => ({
    value: getOptionValue(option),
    label: getOptionLabel(option),
  }));

  const updateSelected = (patch) => {
    if (!selectedBlock) return;
    onElementChange(selectedBlock.id, patch);
  };

  const replaceSelected = (value) => {
    if (!selectedBlock) return;
    onScalarElementChange(selectedBlock.id, value);
  };

  const canRemoveSelected =
    selectedBlock &&
    (!selectedBlock.required || ['shape', 'image', 'nfc'].includes(selectedBlock.type));

  const icon = (type) => {
    if (type === 'text') return <Monitor size={14} />;
    if (type === 'qr') return <QrCode size={14} />;
    if (type === 'nfc') return <Nfc size={14} />;
    if (type === 'shape') return <Box size={14} />;
    return <Sparkles size={14} />;
  };

  return (
    <section className="qr-print-customer-studio-card">
      <div className="qr-print-customer-studio-card__heading">
        <div>
          <span>2D customization</span>
          <h2>Design controls</h2>
        </div>
      </div>

      <div className="qr-print-customer-studio-section">
        <strong>Preview zoom</strong>
        <CustomerNumberControl label="Zoom" value={previewZoom} min={40} max={220} step={5} suffix="%" onChange={onPreviewZoomChange} />
      </div>

      {productSizeOptions.length > 0 ? (
        <div className="qr-print-customer-studio-section qr-print-customer-product-size-section">
          <strong>Product size</strong>
          <p>Choose the print size or adjust it manually. The live preview updates immediately.</p>

          <DesignerSelect
            value={selectedSize}
            onChange={onSizeChange}
            options={productSizeOptions}
            placeholder="Choose size"
          />
        </div>
      ) : null}

      <div className="qr-print-customer-studio-section">
        <strong>Product options</strong>
        {materialOptions.length > 0 ? (
          <DesignerControlRow label="Material">
            <DesignerSelect value={selectedMaterial} onChange={onMaterialChange} options={materialOptions} placeholder="Select material" />
          </DesignerControlRow>
        ) : null}
        {colorOptions.length > 0 ? (
          <DesignerControlRow label="Product color">
            <DesignerSelect value={selectedColor} onChange={onColorChange} options={colorOptions} placeholder="Select color" />
          </DesignerControlRow>
        ) : null}
      </div>

      <div className="qr-print-customer-studio-section">
        <strong>Add elements</strong>
        <div className="qr-print-customer-add-grid">
          <button type="button" onClick={() => onAddBlock('text')}>Text</button>
          <button type="button" onClick={() => onAddBlock('qr')}>QR</button>
          <button type="button" onClick={() => onAddBlock('nfc')}>NFC</button>
          <button type="button" onClick={() => onAddBlock('shape')}>Shape</button>
        </div>
      </div>

      <div className="qr-print-customer-studio-section">
        <strong>Layers</strong>
        <div className="qr-print-customer-layers">
          {editableBlocks.map((block) => (
            <button
              type="button"
              key={block.id}
              className={selectedBlock?.id === block.id ? 'is-active' : ''}
              onClick={() => onSelectBlock(block.id)}
            >
              {icon(block.type)}
              <span>{block.label || block.id}</span>
            </button>
          ))}
        </div>
      </div>

      {selectedBlock ? (
        <div className="qr-print-customer-studio-section">
          <div className="qr-print-customer-selected-title">
            <strong>{selectedBlock.label || selectedBlock.id}</strong>
            {canRemoveSelected ? (
              <button type="button" onClick={() => onRemoveBlock(selectedBlock.id)}>Delete</button>
            ) : null}
          </div>

          {selectedBlock.type === 'background_color' ? (
            <DesignerControlRow label="Background color">
              <input type="color" value={normalizeHexColor(selectedValue, '#ffffff')} onChange={(event) => replaceSelected(event.target.value)} />
            </DesignerControlRow>
          ) : null}

          {selectedBlock.type === 'product_color' ? (
            <DesignerControlRow label="Border color">
              <input type="color" value={normalizeHexColor(selectedValue, '#d97706')} onChange={(event) => replaceSelected(event.target.value)} />
            </DesignerControlRow>
          ) : null}

          {['text', 'logo', 'image', 'qr', 'nfc', 'shape'].includes(selectedBlock.type) ? (
            <div className="qr-print-customer-control-grid">
              <CustomerNumberControl label="X" value={selectedValue.x ?? 50} min={0} max={100} step={0.01} suffix="%" onChange={(value) => updateSelected({ x: value })} />
              <CustomerNumberControl label="Y" value={selectedValue.y ?? 50} min={0} max={100} step={0.01} suffix="%" onChange={(value) => updateSelected({ y: value })} />
            </div>
          ) : null}

          {selectedBlock.type === 'text' ? (
            <>
              <DesignerControlRow label="Text">
                <textarea rows="3" value={selectedValue.text || ''} onChange={(event) => updateSelected({ text: event.target.value })} />
              </DesignerControlRow>
              <div className="qr-print-customer-control-grid">
                <CustomerNumberControl label="Size" value={selectedValue.font_size ?? 4} min={1.2} max={12} step={0.1} onChange={(value) => updateSelected({ font_size: value })} />
                <CustomerNumberControl label="Weight" value={selectedValue.weight ?? 700} min={300} max={950} step={50} onChange={(value) => updateSelected({ weight: value })} />
              </div>
              <DesignerControlRow label="Text color">
                <input type="color" value={normalizeHexColor(selectedValue.color, '#1f2937')} onChange={(event) => updateSelected({ color: event.target.value })} />
              </DesignerControlRow>
              <DesignerControlRow label="Align">
                <DesignerSelect
                  value={selectedValue.align || 'center'}
                  onChange={(value) => updateSelected({ align: value })}
                  options={[
                    { value: 'left', label: 'Left' },
                    { value: 'center', label: 'Center' },
                    { value: 'right', label: 'Right' },
                  ]}
                />
              </DesignerControlRow>
            </>
          ) : null}

          {selectedBlock.type === 'qr' ? (
            <>
              <CustomerNumberControl label="QR size" value={selectedValue.size ?? 34} min={12} max={72} step={1} suffix="%" onChange={(value) => updateSelected({ size: value })} />
              <div className="qr-print-customer-control-grid">
                <DesignerControlRow label="QR color"><input type="color" value={normalizeHexColor(selectedValue.foreground_color, '#111111')} onChange={(event) => updateSelected({ foreground_color: event.target.value })} /></DesignerControlRow>
                <DesignerControlRow label="QR bg"><input type="color" value={normalizeHexColor(selectedValue.background_color, '#ffffff')} onChange={(event) => updateSelected({ background_color: event.target.value })} /></DesignerControlRow>
              </div>
            </>
          ) : null}

          {selectedBlock.type === 'nfc' ? (
            <>
              <CustomerNumberControl label="NFC size" value={selectedValue.size ?? 22} min={10} max={48} step={1} suffix="%" onChange={(value) => updateSelected({ size: value })} />
              <div className="qr-print-customer-control-grid">
                <DesignerControlRow label="Icon"><input type="color" value={normalizeHexColor(selectedValue.color, '#d97706')} onChange={(event) => updateSelected({ color: event.target.value })} /></DesignerControlRow>
                <DesignerControlRow label="Bg"><input type="color" value={normalizeHexColor(selectedValue.background_color, '#fff7ed')} onChange={(event) => updateSelected({ background_color: event.target.value })} /></DesignerControlRow>
              </div>
            </>
          ) : null}

          {(selectedBlock.type === 'logo' || selectedBlock.type === 'image') ? (
            <>
              <CustomerNumberControl label="Width" value={selectedValue.width ?? 22} min={6} max={70} step={1} suffix="%" onChange={(value) => updateSelected({ width: value })} />
              <CustomerNumberControl label="Opacity" value={selectedValue.opacity ?? 1} min={0.1} max={1} step={0.05} onChange={(value) => updateSelected({ opacity: value })} />
            </>
          ) : null}

          {selectedBlock.type === 'shape' ? (
            <>
              <DesignerControlRow label="Shape">
                <DesignerSelect
                  value={selectedValue.shape || 'rectangle'}
                  onChange={(value) => updateSelected({ shape: value })}
                  options={[
                    { value: 'rectangle', label: 'Rectangle' },
                    { value: 'circle', label: 'Circle' },
                    { value: 'line', label: 'Line' },
                  ]}
                />
              </DesignerControlRow>
              <div className="qr-print-customer-control-grid">
                <CustomerNumberControl label="Width" value={selectedValue.width ?? 28} min={2} max={100} step={1} suffix="%" onChange={(value) => updateSelected({ width: value })} />
                <CustomerNumberControl label="Height" value={selectedValue.height ?? 12} min={1} max={100} step={1} suffix="%" onChange={(value) => updateSelected({ height: value })} />
              </div>
              <div className="qr-print-customer-control-grid">
                <DesignerControlRow label="Fill"><input type="color" value={normalizeHexColor(selectedValue.fill_color, '#fff7ed')} onChange={(event) => updateSelected({ fill_color: event.target.value })} /></DesignerControlRow>
                <DesignerControlRow label="Border"><input type="color" value={normalizeHexColor(selectedValue.border_color, '#d97706')} onChange={(event) => updateSelected({ border_color: event.target.value })} /></DesignerControlRow>
              </div>
              <CustomerNumberControl label="Radius" value={selectedValue.radius ?? 14} min={0} max={50} step={1} onChange={(value) => updateSelected({ radius: value })} />
            </>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function DesignerControlRow({ label, children }) {
  return (
    <label className="qr-print-customer-control-row">
      <span>{label}</span>
      {children}
    </label>
  );
}

function CustomerNumberControl({
  label,
  value,
  min,
  max,
  step,
  suffix = '',
  onChange,
}) {
  const safeValue = clamp(value, min, max);

  return (
    <label className="qr-print-customer-number-control">
      <span>
        {label}
        <small>{safeValue}{suffix}</small>
      </span>
      <input type="range" min={min} max={max} step={step} value={safeValue} onChange={(event) => onChange(Number(event.target.value))} />
      <input type="number" min={min} max={max} step={step} value={safeValue} onChange={(event) => onChange(clamp(event.target.value, min, max))} />
    </label>
  );
}

function QrPrintCustomer2DPreview({
  schema,
  configuration,
  extraBlocks = [],
  menuUrl,
  fallbackRestaurantName,
  productType,
  selectedBlockId,
  onSelectBlock,
  onElementChange,
  printSizeCm,
  previewZoom = 100,
  side = 'front',
}) {
  const dragStateRef = useRef(null);
  const panStateRef = useRef(null);
  const safeZoom = clamp(previewZoom, 40, 220);
  const schemaBlocks = getBlocksForDesignerSide(schema, side);
  const blocks = [
    ...schemaBlocks,
    ...extraBlocks.filter((block) => !block.side || block.side === side),
  ];
  const printSize = printSizeCm || { width: 12, height: 18 };
  const previewWidthPx = clamp(printSize.width * 30, 180, 620);
  const backgroundBlock = blocks.find((block) => block.type === 'background_color');
  const productColorBlock = blocks.find((block) => block.type === 'product_color');
  const backgroundValue = backgroundBlock ? getDesignerBlockValue(configuration, backgroundBlock) : configuration.background_color;
  const productColor = productColorBlock ? getDesignerBlockValue(configuration, productColorBlock) : null;
  const draggableBlocks = blocks.filter((block) => ['shape', 'text', 'logo', 'image', 'qr', 'nfc'].includes(block.type));
  const surfaceBackground = typeof backgroundValue === 'string' ? backgroundValue : configuration.background_color || '#ffffff';
  const productBorder = typeof productColor === 'string' && productColor ? productColor : null;

  const getPointerPosition = (event, surface, dragState) => {
    const rect = surface.getBoundingClientRect();
    const x = ((event.clientX - rect.left - dragState.offsetX) / rect.width) * 100;
    const y = ((event.clientY - rect.top - dragState.offsetY) / rect.height) * 100;

    return {
      x: roundDesignNumber(clamp(x, dragState.paddingX, 100 - dragState.paddingX)),
      y: roundDesignNumber(clamp(y, dragState.paddingY, 100 - dragState.paddingY)),
    };
  };

  const startDrag = (event, block) => {
    if (!onElementChange) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const surface = event.currentTarget.closest('[data-customer-2d-surface="true"]');
    if (!surface) return;

    onSelectBlock(block.id);
    const rect = surface.getBoundingClientRect();
    const value = getDesignerBlockValue(configuration, block);
    const x = clamp(value.x, 0, 100);
    const y = clamp(value.y, 0, 100);
    const padding = getDragPadding(block, value);
    const dragState = {
      blockId: block.id,
      surface,
      paddingX: padding.x,
      paddingY: padding.y,
      offsetX: event.clientX - (rect.left + (x / 100) * rect.width),
      offsetY: event.clientY - (rect.top + (y / 100) * rect.height),
    };

    const moveDrag = (moveEvent) => {
      if (!dragStateRef.current) return;
      onElementChange(dragState.blockId, getPointerPosition(moveEvent, dragState.surface, dragState));
    };

    const stopDrag = () => {
      window.removeEventListener('pointermove', moveDrag);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);
      dragStateRef.current = null;
    };

    dragState.cleanup = stopDrag;
    dragStateRef.current = dragState;
    window.addEventListener('pointermove', moveDrag);
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);

    event.preventDefault();
    event.stopPropagation();
  };

  const startPan = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target?.closest('[data-customer-2d-draggable="true"]')) return;

    const stage = event.currentTarget;
    stage.focus({ preventScroll: true });
    const panState = {
      stage,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: stage.scrollLeft,
      scrollTop: stage.scrollTop,
    };

    const movePan = (moveEvent) => {
      if (!panStateRef.current) return;
      panState.stage.scrollLeft = panState.scrollLeft - (moveEvent.clientX - panState.startX);
      panState.stage.scrollTop = panState.scrollTop - (moveEvent.clientY - panState.startY);
    };

    const stopPan = () => {
      stage.classList.remove('is-panning');
      window.removeEventListener('pointermove', movePan);
      window.removeEventListener('pointerup', stopPan);
      window.removeEventListener('pointercancel', stopPan);
      panStateRef.current = null;
    };

    panState.cleanup = stopPan;
    panStateRef.current = panState;
    stage.classList.add('is-panning');
    window.addEventListener('pointermove', movePan);
    window.addEventListener('pointerup', stopPan);
    window.addEventListener('pointercancel', stopPan);
    event.preventDefault();
  };

  const handleKeyDown = (event) => {
    const step = event.shiftKey ? 120 : 42;
    let handled = true;

    if (event.key === 'ArrowLeft') event.currentTarget.scrollLeft -= step;
    else if (event.key === 'ArrowRight') event.currentTarget.scrollLeft += step;
    else if (event.key === 'ArrowUp') event.currentTarget.scrollTop -= step;
    else if (event.key === 'ArrowDown') event.currentTarget.scrollTop += step;
    else handled = false;

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  useEffect(() => () => {
    dragStateRef.current?.cleanup?.();
    panStateRef.current?.cleanup?.();
  }, []);

  return (
    <div className="qr-print-customer-preview-wrap">
      <div className="qr-print-customer-preview-stage" tabIndex={0} role="region" aria-label="2D design preview" onPointerDown={startPan} onKeyDown={handleKeyDown}>
        <div className="qr-print-customer-print-frame" style={{ '--customer-preview-zoom': safeZoom / 100, '--customer-preview-width': `${previewWidthPx}px`, '--customer-preview-aspect': `${printSize.width} / ${printSize.height}` }}>
          <div className="qr-print-customer-ruler-top"><span>&lt;--- {formatCm(printSize.width)} ---&gt;</span></div>
          <div className="qr-print-customer-ruler-body">
            <div className="qr-print-customer-ruler-left"><span>&lt;--- {formatCm(printSize.height)} ---&gt;</span></div>
            <div className={`qr-print-customer-design-surface is-${productType || 'table_stand'}`} data-customer-2d-surface="true" style={{ aspectRatio: `${printSize.width} / ${printSize.height}`, backgroundColor: surfaceBackground, borderColor: productBorder || undefined }}>
              {draggableBlocks.map((block) => (
                <QrPrintCustomer2DElement key={block.id} block={block} value={getDesignerBlockValue(configuration, block)} menuUrl={menuUrl} fallbackRestaurantName={fallbackRestaurantName} selected={selectedBlockId === block.id} onSelect={() => onSelectBlock(block.id)} onPointerDown={(event) => startDrag(event, block)} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QrPrintCustomer2DElement({ block, value, menuUrl, fallbackRestaurantName, selected, onSelect, onPointerDown }) {
  const baseClass = `qr-print-customer-preview-element ${selected ? 'is-selected' : ''}`;

  if (block.type === 'shape') {
    const shape = value.shape || 'rectangle';

    return <button type="button" className={`${baseClass} is-shape is-${shape}`} data-customer-2d-draggable="true" style={{ left: `${clamp(value.x, 0, 100)}%`, top: `${clamp(value.y, 0, 100)}%`, width: `${clamp(value.width, 2, 100)}%`, height: `${clamp(value.height, 1, 100)}%`, borderRadius: shape === 'circle' || shape === 'line' ? '999px' : `${clamp(value.radius, 0, 50)}px`, backgroundColor: value.fill_color || '#fff7ed', borderColor: value.border_color || '#d97706', opacity: value.opacity ?? 1 }} onClick={onSelect} onPointerDown={onPointerDown} aria-label="Move shape" />;
  }

  if (block.type === 'text') {
    const align = value.align || 'center';
    const text = value.text || (block.id === 'restaurant-name' ? fallbackRestaurantName : block.label || 'Text');

    return <button type="button" className={`${baseClass} is-text`} data-customer-2d-draggable="true" style={{ left: `${clamp(value.x, 0, 100)}%`, top: `${clamp(value.y, 0, 100)}%`, transform: getTextTransform(align), color: value.color || '#1f2937', fontSize: `${clamp(value.font_size, 1.4, 12) * 3.4}px`, fontWeight: Number(value.weight || 700), textAlign: align }} onClick={onSelect} onPointerDown={onPointerDown} aria-label={`Move ${block.label || 'text'}`}>{text}</button>;
  }

  if (block.type === 'qr') {
    const size = clamp(value.size, 12, 72);

    return <button type="button" className={`${baseClass} is-qr`} data-customer-2d-draggable="true" style={{ left: `${clamp(value.x, 0, 100)}%`, top: `${clamp(value.y, 0, 100)}%`, width: `${size}%`, backgroundColor: value.background_color || '#ffffff' }} onClick={onSelect} onPointerDown={onPointerDown} aria-label="Move QR code"><QRCodeSVG value={menuUrl} size={512} level="H" marginSize={0} bgColor={value.background_color || '#ffffff'} fgColor={value.foreground_color || '#111111'} /></button>;
  }

  if (block.type === 'nfc') {
    const size = clamp(value.size, 10, 48);

    return <button type="button" className={`${baseClass} is-nfc`} data-customer-2d-draggable="true" style={{ left: `${clamp(value.x, 0, 100)}%`, top: `${clamp(value.y, 0, 100)}%`, width: `${size}%`, color: value.color || '#d97706', backgroundColor: value.background_color || '#fff7ed' }} onClick={onSelect} onPointerDown={onPointerDown} aria-label="Move NFC mark"><Nfc size={28} /><span>NFC</span></button>;
  }

  if (block.type === 'logo' || block.type === 'image') {
    const width = clamp(value.width, 6, 70);

    return <button type="button" className={`${baseClass} is-image`} data-customer-2d-draggable="true" style={{ left: `${clamp(value.x, 0, 100)}%`, top: `${clamp(value.y, 0, 100)}%`, width: `${width}%`, opacity: value.opacity ?? 1 }} onClick={onSelect} onPointerDown={onPointerDown} aria-label={`Move ${block.label || block.type}`}>{value.src ? <img src={value.src} alt={block.label || block.type} /> : <span>{block.type === 'logo' ? 'LOGO' : 'IMAGE'}</span>}</button>;
  }

  return null;
}

